/**
 * Chart Legend and Status Line Value Formatters.
 *
 * Formats candlestick OHLC, volume, and percentage changes against previous close
 * for the status line overlay.
 */

export interface OhlcFormatted {
  open: string;
  high: string;
  low: string;
  close: string;
}

export interface ChangeFormatted {
  text: string;
  color: string;
  isPositive: boolean;
  change: number;
  pctChange: number;
}

export interface StatusLineSettings {
  title?: boolean;
  chartValues?: boolean;
  change?: boolean;
  volume?: boolean;
  [key: string]: any;
}

export interface StatusLineViewModel {
  showTitle: boolean;
  title: string;
  timeLabel: string;
  showChartValues: boolean;
  ohlc: OhlcFormatted;
  showVolume: boolean;
  volume: string;
  showChange: boolean;
  change: ChangeFormatted | null;
}

/**
 * Formats candlestick OHLC values to 2 decimal places.
 */
export function formatOhlcValues(candle: {
  open?: number;
  high?: number;
  low?: number;
  close?: number;
}): OhlcFormatted {
  return {
    open: (candle?.open ?? 0).toFixed(2),
    high: (candle?.high ?? 0).toFixed(2),
    low: (candle?.low ?? 0).toFixed(2),
    close: (candle?.close ?? 0).toFixed(2),
  };
}

/**
 * Formats volume numbers with commas (e.g. 1,234,567).
 */
export function formatVolumeValue(
  volValue: number | null | undefined,
): string {
  return volValue != null ? Math.round(volValue).toLocaleString("en-US") : "0";
}

/**
 * Computes price change and percentage change against the previous bar's close.
 * Returns #2DBE8E for positive changes and #F06A6A for negative changes.
 */
export function formatChangeValue(
  close: number,
  prevClose: number | null | undefined,
): ChangeFormatted | null {
  if (prevClose == null || prevClose === 0) return null;
  const change = close - prevClose;
  const pctChange = (change / prevClose) * 100;
  const sign = change >= 0 ? "+" : "";
  const color = change >= 0 ? "#2DBE8E" : "#F06A6A";
  const text = `Chg: ${sign}${change.toFixed(2)} (${sign}${pctChange.toFixed(2)}%)`;
  return {
    text,
    color,
    isPositive: change >= 0,
    change,
    pctChange,
  };
}

/**
 * Formats the header title of the status line.
 */
export function formatStatusLineTitle(
  displayName: string,
  timeframe: string,
): string {
  return `${displayName || ""} · ${timeframe || ""}`;
}

/**
 * Resolves a raw candlestick for Line/Area crosshair points that only have { time, value }.
 */
export function resolveRawCandle(
  candle: any,
  allCandles?: Array<{
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
  }>,
): { time: number; open: number; high: number; low: number; close: number } | null {
  if (!candle) return null;
  if (candle.close == null && candle.value != null) {
    const raw =
      candle.time != null && Array.isArray(allCandles)
        ? allCandles.find((c) => c.time === candle.time)
        : null;
    return (
      raw || {
        time: candle.time,
        open: candle.value,
        high: candle.value,
        low: candle.value,
        close: candle.value,
      }
    );
  }
  return candle;
}

/**
 * Builds the complete status line view model honoring visibility settings.
 */
export function buildStatusLineViewModel(
  candle: any,
  volValue: number | null | undefined,
  prevClose: number | null | undefined,
  displayName: string,
  timeframe: string,
  settings: StatusLineSettings = {},
  timeLabel: string = "",
  allCandles?: Array<{
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
  }>,
): StatusLineViewModel {
  const resolved =
    resolveRawCandle(candle, allCandles) || { open: 0, high: 0, low: 0, close: 0 };
  const showTitle = settings.title ?? true;
  const showChartValues = settings.chartValues ?? true;
  const showVolume = settings.volume ?? true;
  const showChange = settings.change ?? true;

  return {
    showTitle,
    title: showTitle ? formatStatusLineTitle(displayName, timeframe) : "",
    timeLabel: timeLabel || "",
    showChartValues,
    ohlc: formatOhlcValues(resolved),
    showVolume,
    volume: formatVolumeValue(volValue),
    showChange,
    change: formatChangeValue(resolved.close, prevClose),
  };
}
